import React, { useState, useEffect } from 'react';
// [Web UX 3. csomag (D), 2026-10-04] A kérés-hibák szövege a közös `apiErrorMessage`-en át megy (a szerver magyar
// indoklása mindhárom törzs-alakból / hálózati hibánál magyar mondat / különben „<tartalék> (HTTP n)"),
// a nyers `err.response?.data?.message` helyett. Őr: `utils/__tests__/rawErrorMessages.guard.test.ts`.
import { apiErrorMessage } from '../../utils/apiErrors';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
} from '@mui/icons-material';
import coachAvailabilityService, { CalendarCoachAvailabilityDto, VenueDto } from '../../services/coachAvailabilityService';
import eventService from '../../services/eventService';
import { userService } from '../../services/userService';
import { getTodayLocal } from '../../utils/dateUtils';
import CoachAvailabilityDetailsDialog from './CoachAvailabilityDetailsDialog';
import CoachAvailabilityEditDialog from './CoachAvailabilityEditDialog';
import CoachAvailabilityCreateDialog from './CoachAvailabilityCreateDialog';
import { NAV_BUTTON_TOUCH_SX } from '../Calendar/calendarA11y';

interface WeeklyViewProps {
  selectedDate: string;
  onDateChange: (date: string) => void;
  userRole: string;
}

interface DayColumn {
  date: string;
  dayName: string;
  dayNumber: number;
  isToday: boolean;
  availabilities: CalendarCoachAvailabilityDto[];
}

interface UserDto {
  userId: string;
  name: string;
  userRoleDisplayName: string;
  isActive: boolean;
}

const CoachAvailabilityWeeklyView: React.FC<WeeklyViewProps> = ({
  selectedDate,
  onDateChange,
  userRole,
}) => {
  const [weekDays, setWeekDays] = useState<DayColumn[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [weekStart, setWeekStart] = useState<string>('');

  // Filter states
  const [venues, setVenues] = useState<VenueDto[]>([]);
  const [coaches, setCoaches] = useState<UserDto[]>([]);
  const [selectedVenue, setSelectedVenue] = useState<string>('');
  const [selectedCoach, setSelectedCoach] = useState<string>('');

  // Dialog states
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedAvailabilityId, setSelectedAvailabilityId] = useState<string | null>(null);
  const [createDate, setCreateDate] = useState<string>('');

  const isAdminOrCoach = userRole === 'Administrator' || userRole === 'Coach';

  // Get the start of the week (Monday) for a given date
  const getWeekStart = (dateStr: string): string => {
    // Parse date parts to avoid timezone issues
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    const dayOfWeek = date.getDay(); // 0=Sunday, 1=Monday, 2=Tuesday, ..., 6=Saturday

    // Calculate days to subtract to get to Monday
    // If Sunday (0), go back 6 days; if Monday (1), go back 0 days; if Tuesday (2), go back 1 day, etc.
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

    // Create Monday date
    const monday = new Date(year, month - 1, day - daysToMonday);

    // Format as YYYY-MM-DD using local date
    const mondayYear = monday.getFullYear();
    const mondayMonth = String(monday.getMonth() + 1).padStart(2, '0');
    const mondayDay = String(monday.getDate()).padStart(2, '0');
    return `${mondayYear}-${mondayMonth}-${mondayDay}`;
  };

  // Calculate week days from week start
  const calculateWeekDays = (startDate: string): DayColumn[] => {
    const days: DayColumn[] = [];
    const today = getTodayLocal();
    const dayNames = ['Hétfő', 'Kedd', 'Szerda', 'Csütörtök', 'Péntek', 'Szombat', 'Vasárnap'];

    // Parse startDate using local timezone
    const [year, month, day] = startDate.split('-').map(Number);

    for (let i = 0; i < 7; i++) {
      // Create date in local timezone
      const date = new Date(year, month - 1, day + i);

      // Format as YYYY-MM-DD
      const dateYear = date.getFullYear();
      const dateMonth = String(date.getMonth() + 1).padStart(2, '0');
      const dateDay = String(date.getDate()).padStart(2, '0');
      const dateStr = `${dateYear}-${dateMonth}-${dateDay}`;

      days.push({
        date: dateStr,
        dayName: dayNames[i],
        dayNumber: date.getDate(),
        isToday: dateStr === today,
        availabilities: [],
      });
    }

    return days;
  };

  // Load initial data (venues and coaches)
  const loadInitialData = async () => {
    try {
      const [venueData, coachData] = await Promise.all([
        eventService.getVenues(),
        userService.getCoaches()
      ]);

      setVenues(Array.isArray(venueData) ? venueData : []);
      setCoaches(Array.isArray(coachData) ? coachData : []);
    } catch (err) {
      console.error('Error loading initial data:', err);
    }
  };

  // Load availabilities for the week
  const loadWeekAvailabilities = async () => {
    if (!weekStart) return;

    setLoading(true);
    setError(null);

    try {
      // Parse weekStart using local timezone
      const [year, month, day] = weekStart.split('-').map(Number);
      const endDate = new Date(year, month - 1, day + 6);

      // Format as YYYY-MM-DD
      const endYear = endDate.getFullYear();
      const endMonth = String(endDate.getMonth() + 1).padStart(2, '0');
      const endDay = String(endDate.getDate()).padStart(2, '0');
      const endDateStr = `${endYear}-${endMonth}-${endDay}`;

      const availabilities = await coachAvailabilityService.getDateRangeAvailabilities(
        weekStart,
        endDateStr,
        selectedCoach || undefined,
        selectedVenue || undefined
      );

      // Distribute availabilities to their respective days
      const updatedDays = weekDays.map((day) => ({
        ...day,
        availabilities: availabilities.filter((avail) => {
          // Parse availability date using local timezone
          const availDate = avail.availabilityDate.split('T')[0];
          return availDate === day.date;
        }),
      }));

      setWeekDays(updatedDays);
    } catch (err: any) {
      console.error('Error loading week availabilities:', err);
      setError(apiErrorMessage(err, 'Hiba az elérhetőségek betöltésekor'));
    } finally {
      setLoading(false);
    }
  };

  // Load initial data on mount
  useEffect(() => {
    loadInitialData();
  }, []);

  // Initialize week when selectedDate changes
  useEffect(() => {
    if (selectedDate) {
      const start = getWeekStart(selectedDate);
      setWeekStart(start);
      setWeekDays(calculateWeekDays(start));
    }
  }, [selectedDate]);

  // Load availabilities when week or filters change
  useEffect(() => {
    if (weekStart) {
      loadWeekAvailabilities();
    }
  }, [weekStart, selectedCoach, selectedVenue]);

  // Navigate to previous week
  const handlePreviousWeek = () => {
    if (weekStart) {
      // Parse weekStart using local timezone
      const [year, month, day] = weekStart.split('-').map(Number);
      const newDate = new Date(year, month - 1, day - 7);

      // Format as YYYY-MM-DD
      const newYear = newDate.getFullYear();
      const newMonth = String(newDate.getMonth() + 1).padStart(2, '0');
      const newDay = String(newDate.getDate()).padStart(2, '0');
      const newDateStr = `${newYear}-${newMonth}-${newDay}`;

      onDateChange(newDateStr);
    }
  };

  // Navigate to next week
  const handleNextWeek = () => {
    if (weekStart) {
      // Parse weekStart using local timezone
      const [year, month, day] = weekStart.split('-').map(Number);
      const newDate = new Date(year, month - 1, day + 7);

      // Format as YYYY-MM-DD
      const newYear = newDate.getFullYear();
      const newMonth = String(newDate.getMonth() + 1).padStart(2, '0');
      const newDay = String(newDate.getDate()).padStart(2, '0');
      const newDateStr = `${newYear}-${newMonth}-${newDay}`;

      onDateChange(newDateStr);
    }
  };

  // Navigate to today
  const handleToday = () => {
    onDateChange(getTodayLocal());
  };

  // Open details dialog
  const handleAvailabilityClick = (availabilityId: string) => {
    setSelectedAvailabilityId(availabilityId);
    setDetailsDialogOpen(true);
  };

  // Open create dialog
  const handleDayClick = (date: string) => {
    if (isAdminOrCoach) {
      setCreateDate(date);
      setCreateDialogOpen(true);
    }
  };

  // Format time range
  const formatTimeRange = (startTime: string, endTime: string): string => {
    return `${startTime.substring(0, 5)} - ${endTime.substring(0, 5)}`;
  };

  // Get week range display
  const getWeekRangeDisplay = (): string => {
    if (weekDays.length === 0) return '';
    const start = weekDays[0];
    const end = weekDays[6];
    return `${start.dayNumber}. ${start.dayName.substring(0, 3)} - ${end.dayNumber}. ${end.dayName.substring(0, 3)}`;
  };

  return (
    <Box>
      {/* Header with navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ fontSize: { xs: '1rem', sm: '1.5rem' }, whiteSpace: 'nowrap' }}>
          Heti Nézet: {getWeekRangeDisplay()}
        </Typography>

        {/* [Web UX 9 / T5, 2026-10-13] címke nélküli lapozók (`CoachAvailabilityWeeklyView.tsx:287-295`, UX-lista 6.1) → aria-label
            + telefonon 44 px, a naptár heti nézetével egyező feliratok. Őr: `utils/__tests__/iconButtonLabels.guard.test.ts`. */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton onClick={handlePreviousWeek} color="primary" size="small" aria-label="Előző hét" sx={NAV_BUTTON_TOUCH_SX}>
            <ChevronLeftIcon />
          </IconButton>
          <IconButton onClick={handleToday} color="primary" size="small" aria-label="Mai nap" sx={NAV_BUTTON_TOUCH_SX}>
            <TodayIcon />
          </IconButton>
          <IconButton onClick={handleNextWeek} color="primary" size="small" aria-label="Következő hét" sx={NAV_BUTTON_TOUCH_SX}>
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 150 }, flex: { xs: 1, sm: 'none' } }}>
          <InputLabel>Helyszín</InputLabel>
          <Select
            value={selectedVenue}
            label="Helyszín"
            onChange={(e: SelectChangeEvent) => setSelectedVenue(e.target.value)}
          >
            <MenuItem value="">Összes</MenuItem>
            {venues.map((venue) => (
              <MenuItem key={venue.venueId} value={venue.venueId}>
                {venue.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 150 }, flex: { xs: 1, sm: 'none' } }}>
          <InputLabel>Edző</InputLabel>
          <Select
            value={selectedCoach}
            label="Edző"
            onChange={(e: SelectChangeEvent) => setSelectedCoach(e.target.value)}
          >
            <MenuItem value="">Összes</MenuItem>
            {coaches.map((coach) => (
              <MenuItem key={coach.userId} value={coach.userId}>
                {coach.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Error alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading indicator */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Week grid */}
      {!loading && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(7, 1fr)',
            },
            gap: 0.25,
          }}
        >
          {weekDays.map((day) => (
            <Paper
              key={day.date}
              sx={{
                p: 0.5,
                minHeight: 150,
                cursor: isAdminOrCoach ? 'pointer' : 'default',
                bgcolor: day.isToday ? 'action.selected' : 'background.paper',
                border: day.isToday ? '2px solid' : '1px solid',
                borderColor: day.isToday ? 'primary.main' : 'divider',
                '&:hover': isAdminOrCoach
                  ? {
                      bgcolor: 'action.hover',
                    }
                  : {},
              }}
              onClick={() => handleDayClick(day.date)}
            >
                {/* Day header */}
                <Box sx={{ mb: 0.5, borderBottom: '1px solid', borderColor: 'divider', pb: 0.25 }}>
                  <Typography variant="caption" color="text.secondary" fontSize="0.6rem">
                    {day.dayName}
                  </Typography>
                  <Typography variant="subtitle2" fontWeight="medium" fontSize="0.9rem">
                    {day.dayNumber}
                    {day.isToday && (
                      <Chip
                        label="Ma"
                        size="small"
                        color="primary"
                        sx={{ ml: 0.25, height: 16, fontSize: '0.55rem' }}
                      />
                    )}
                  </Typography>
                </Box>

                {/* Availabilities */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  {day.availabilities.length === 0 && (
                    <Typography variant="caption" color="text.secondary" align="center" fontSize="0.6rem">
                      Nincs elérhetőség
                    </Typography>
                  )}

                  {day.availabilities.map((availability) => (
                    <Paper
                      key={availability.availabilityId}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAvailabilityClick(availability.availabilityId);
                      }}
                      sx={{
                        p: 0.5,
                        cursor: 'pointer',
                        bgcolor: 'primary.light',
                        color: 'primary.contrastText',
                        '&:hover': {
                          bgcolor: 'primary.main',
                          opacity: 0.9,
                        },
                      }}
                    >
                      <Typography variant="caption" fontWeight="medium" sx={{ mb: 0.125, display: 'block', fontSize: '0.65rem', color: 'inherit' }}>
                        {availability.coachName}
                      </Typography>

                      <Typography variant="caption" display="block" fontSize="0.6rem" sx={{ color: 'inherit' }}>
                        {formatTimeRange(availability.startTime, availability.endTime)}
                      </Typography>

                      {availability.notes && (
                        <Tooltip title={availability.notes}>
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{
                              mt: 0.125,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontSize: '0.55rem',
                              color: 'inherit',
                            }}
                          >
                            {availability.notes}
                          </Typography>
                        </Tooltip>
                      )}
                    </Paper>
                  ))}
                </Box>
              </Paper>
          ))}
        </Box>
      )}

      {/* Dialogs */}
      <CoachAvailabilityDetailsDialog
        open={detailsDialogOpen}
        onClose={() => {
          setDetailsDialogOpen(false);
          setSelectedAvailabilityId(null);
        }}
        availabilityId={selectedAvailabilityId}
        onEdit={(id) => {
          setDetailsDialogOpen(false);
          setSelectedAvailabilityId(id);
          setEditDialogOpen(true);
        }}
        onDelete={() => {
          setDetailsDialogOpen(false);
          setSelectedAvailabilityId(null);
          loadWeekAvailabilities();
        }}
      />

      <CoachAvailabilityEditDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedAvailabilityId(null);
        }}
        availabilityId={selectedAvailabilityId}
        onSuccess={() => {
          setEditDialogOpen(false);
          setSelectedAvailabilityId(null);
          loadWeekAvailabilities();
        }}
      />

      <CoachAvailabilityCreateDialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          setCreateDate('');
        }}
        onSuccess={() => {
          setCreateDialogOpen(false);
          setCreateDate('');
          loadWeekAvailabilities();
        }}
        initialDate={createDate}
        userRole={userRole}
      />
    </Box>
  );
};

export default CoachAvailabilityWeeklyView;
